import { useState, useEffect, useCallback } from 'react';
import { fetchHousehold, upsertHousehold, deleteHousehold } from '@/api/householdApi';
import { useSession } from '@/contexts/AuthContext';
import type { HouseholdInput } from '@/types';
import type { StoredHousehold, HouseholdUpsertInput } from '@/types/household';
import { storedHouseholdToInput, householdInputToUpsert } from '@/types/household';
import { validateEntriesBeforeSave } from '@/utils/entryValidation';
import { isInCurrentTaxYear } from '@/utils/taxYear';

interface UseHouseholdResult {
  household: HouseholdInput | null;
  loading: boolean;
  error: Error | null;
  saveHousehold: (input: HouseholdInput) => Promise<void>;
  deleteHouseholdData: () => Promise<void>;
  refetch: () => Promise<void>;
}

/**
 * Hook for managing household data via Django backend.
 * Automatically fetches household on mount if user is authenticated.
 */
export function useHousehold(): UseHouseholdResult {
  const { user, loading: authLoading } = useSession();
  const [household, setHousehold] = useState<HouseholdInput | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    // Wait for auth to finish loading
    if (authLoading) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (!user) {
        setHousehold(null);
        return;
      }

      const stored = await fetchHousehold();

      if (stored) {
        setHousehold(storedHouseholdToInput(stored));
      } else {
        setHousehold(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch household'));
      setHousehold(null);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveHousehold = useCallback(async (input: HouseholdInput): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Merge with existing tracker entries if not provided
      const mergedInput: HouseholdInput =
        input.tracker_manual_entries === undefined && household?.tracker_manual_entries !== undefined
          ? { ...input, tracker_manual_entries: household.tracker_manual_entries }
          : input;

      const entries = mergedInput.tracker_manual_entries || [];

      // Enforce UK current tax-year window for manual entries (UI also validates)
      const anyOutOfYear = entries.some((e) => {
        const date = (e as { date?: string }).date;
        return !date || !isInCurrentTaxYear(date);
      });
      if (anyOutOfYear) {
        throw new Error('Entries must be within the current UK tax year');
      }

      // Validate entries before saving (will throw if invalid)
      // The database constraints will enforce data integrity as the final safeguard
      validateEntriesBeforeSave(entries);

      const upsertData: HouseholdUpsertInput = householdInputToUpsert(mergedInput);
      const stored: StoredHousehold = await upsertHousehold(upsertData);

      setHousehold(storedHouseholdToInput(stored));
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to save household');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [household]);

  const deleteHouseholdData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      await deleteHousehold();
      setHousehold(null);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to delete household');
      setError(error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    household,
    loading,
    error,
    saveHousehold,
    deleteHouseholdData,
    refetch: fetchData,
  };
}
